import { HttpErrorResponse } from '@angular/common/http';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Buffer } from 'buffer';
import { ToastrService } from 'ngx-toastr';
import { AuthUploadUserService } from 'src/app/_services/auth-upload-user.service';
import { IUploadLinkCodeRejection, UploadLinkCodeRejectionReason } from 'src/app/_models/upload-link-code-rejection';
import { LoginDTO } from '../../_models/Login';
import { EXPIRED_REJECTION_REASONS, UNEXPECTED_ERROR, WRONG_PASSWORD_ERROR } from './login-otp.constants';

@Component({
  selector: 'app-login-otp',
  templateUrl: './login-otp.component.html',
  styleUrls: ['./login-otp.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginOtpComponent {
  loginForm: FormGroup;
  isError: boolean = false;
  model: LoginDTO = {
    email: '',
    password: '',
  };
  email = '';
  code = '';
  tenantName = '';
  source = '';
  passwordError: string = '';
  emailError: string = '';
  isOtp = false;
  emailRegExp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  isLinkExpired = false;

  constructor(
    private fb: FormBuilder,
    private authUploadUserService: AuthUploadUserService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService
  ) {
    (window as any).Buffer = Buffer;
    this.loginForm = this.fb.group({
      email: [this.model.email, [Validators.required, Validators.pattern(this.emailRegExp)]],
      password: [this.model.password, Validators.required],
    });
    this.code = this.route.snapshot.paramMap.get('id') ?? '';
    this.tenantName = this.route.snapshot.queryParams['tenant'] ?? '';
    this.source = this.route.snapshot.queryParams['source'] ?? '';
  }

  getNewPassword() {
    this.toastr.success('Prüfen Sie Ihre E-Mail auf ein neues Passwort', 'Neues Passwort wurde gesendet');
    this.checkEmail();
  }

  checkEmail() {
    const body = {
      email: this.loginForm.get('email')!.value,
      parameter: this.code,
    };
    this.emailError = '';
    this.isLinkExpired = false;
    this.authUploadUserService.checkCode(body).subscribe({
      next: (res) => {
        this.isOtp = res;
        this.email = body.email;
      },
      error: (err: HttpErrorResponse) => {
        const rejection = this.getRejection(err);
        this.isLinkExpired = this.isExpired(rejection);
        this.emailError = rejection?.message ?? UNEXPECTED_ERROR;
      },
    });
  }

  checkPassword() {
    const body = {
      email: this.loginForm.get('email')!.value,
      parameter: this.loginForm.get('password')!.value,
    };
    this.authUploadUserService.loginByCode(body).subscribe({
      next: (res) => {
        const otpRegisterCode = Buffer.from(`${body.email}:${body.parameter}`).toString('base64');
        localStorage.setItem('otp-register-code', otpRegisterCode);
        localStorage.setItem('otp-code', res.code);
        localStorage.setItem('otp-link-id', res.id);
        localStorage.setItem('otp-name', this.tenantName);
        localStorage.setItem('otp-source', this.source);
        localStorage.setItem('otp-user-id', res.userId);
        localStorage.setItem('otp-id', this.code);
        this.router.navigate(['otp/upload']);
      },
      error: (err: HttpErrorResponse) => {
        const rejection = this.getRejection(err);
        if (!rejection) {
          this.passwordError = UNEXPECTED_ERROR;
        } else {
          this.passwordError = this.isExpired(rejection) ? rejection.message : WRONG_PASSWORD_ERROR;
        }
      },
    });
  }

  private getRejection(err: HttpErrorResponse): IUploadLinkCodeRejection | undefined {
    const body = err.error;
    const isRejection =
      typeof body?.message === 'string' && Object.values(UploadLinkCodeRejectionReason).includes(body?.reason);
    return isRejection ? body : undefined;
  }

  private isExpired(rejection: IUploadLinkCodeRejection | undefined): boolean {
    return !!rejection && EXPIRED_REJECTION_REASONS.includes(rejection.reason);
  }
}
